import CloseIcon from '@mui/icons-material/Close';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import TableChartRoundedIcon from '@mui/icons-material/TableChartRounded';
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import {
  GridDensity,
  gridFilteredSortedRowIdsSelector,
  GridFilterModel,
  GridPaginationModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { kebabCase } from 'es-toolkit';
import React from 'react';

import {
  DEFAULT_READ_ONLY_GRID_FILTER_MODEL,
  hasActiveReadOnlyGridFilters,
  ReadOnlyDataGrid,
} from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import { SectionTable } from 'interfaces/agentSession';
import { downloadBlob } from 'utils/exports';

import { useJobResultAugmentation } from 'utils/jobResultAugmentation';
import JobResultSlideLabelModeSelect from './JobResultSlideLabelModeSelect';
import StudyReportCardHeader from './StudyReportCardHeader';
import StudyReportDescriptionMarkdown from './StudyReportDescriptionMarkdown';
import {
  buildStudyReportAutosizedColumns,
  buildStudyReportTableAutosizeOptions,
  buildStudyReportTableCsvContent,
  buildStudyReportTableExportFileName,
  buildStudyReportTableRows,
} from './studyReportTableGrid';
import { useStudyReportTableViewerLinks } from './useStudyReportTableViewerLinks';

interface SectionTableCardProps {
  table: SectionTable;
  studyId?: string;
  onRemove?: () => void;
  beforeGrid?: React.ReactNode;
  action?: React.ReactNode;
  sourceLabel?: string | null;
}

export const SectionTableCardSkeleton: React.FC = () => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack spacing={1.5}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack spacing={0.25} sx={{ flex: 1 }}>
          <Skeleton variant="text" width="35%" height={24} />
          <Skeleton variant="text" width="50%" height={16} />
        </Stack>
      </Stack>
      <Skeleton variant="rounded" height={400} />
      <Skeleton variant="rounded" width={80} height={24} />
    </Stack>
  </Paper>
);

const DASH_REGEX = /-/g;

const TABLE_PAGE_SIZE = 10;
const DISABLE_VIRTUALIZATION_FOR_TESTS = process.env.NODE_ENV === 'test';
const TABLE_EMPTY_STATE_GRID_HEIGHT = 320;
const TABLE_OVERLAY_HEIGHT = 'clamp(180px, 28vh, 260px)';
const FULLSCREEN_TABLE_OVERLAY_HEIGHT = 'clamp(220px, 36vh, 360px)';

const StudyReportSectionTableCard: React.FC<SectionTableCardProps> = ({
  table,
  studyId,
  onRemove,
  beforeGrid,
  action,
  sourceLabel,
}) => {
  const rows = React.useMemo(() => buildStudyReportTableRows(table), [table]);
  const {
    tableAugmentation,
    augmentedDescription,
    showSlideLabelControl,
    slideLabelMode,
    slideLabelOptions,
    setSlideLabelMode,
  } = useJobResultAugmentation({
    studyId,
    sourceConfig: table.sourceConfig,
    table,
    description: table.description,
  });
  const displayDescription = augmentedDescription ?? table.description;
  const viewerLinkResolver = useStudyReportTableViewerLinks({ table, rows, studyId });
  const columns = React.useMemo(
    () => buildStudyReportAutosizedColumns(table, tableAugmentation, viewerLinkResolver),
    [table, tableAugmentation, viewerLinkResolver]
  );
  const autosizeOptions = React.useMemo(
    () => buildStudyReportTableAutosizeOptions(table, tableAugmentation, viewerLinkResolver),
    [table, tableAugmentation, viewerLinkResolver]
  );
  const csvFileName = React.useMemo(() => buildStudyReportTableExportFileName(table), [table]);
  const apiRef = useGridApiRef();
  const [density, setDensity] = React.useState<GridDensity>('compact');
  const [filterModel, setFilterModel] = React.useState<GridFilterModel>(DEFAULT_READ_ONLY_GRID_FILTER_MODEL);
  const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({
    page: 0,
    pageSize: TABLE_PAGE_SIZE,
  });
  const [actionsAnchorEl, setActionsAnchorEl] = React.useState<HTMLElement | null>(null);
  const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);
  const fitsOnOnePage = rows.length <= paginationModel.pageSize;
  const hasActiveFilters = React.useMemo(() => hasActiveReadOnlyGridFilters(filterModel), [filterModel]);
  const resolvedSourceLabel = React.useMemo(() => {
    if (sourceLabel === null) {
      return null;
    }

    return sourceLabel ?? kebabCase(table.sourceType).replace(DASH_REGEX, ' ');
  }, [sourceLabel, table.sourceType]);
  const hasRenderableContent = columns.length > 0;
  const actionsMenuOpen = Boolean(actionsAnchorEl);

  const handleOpenActionsMenu = React.useCallback((event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  }, []);

  const handleCloseActionsMenu = React.useCallback(() => {
    setActionsAnchorEl(null);
  }, []);

  const handleDownloadCsv = React.useCallback(() => {
    handleCloseActionsMenu();
    const csvContent = buildStudyReportTableCsvContent(table, undefined, tableAugmentation);
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    downloadBlob(csvBlob, `${csvFileName}.csv`);
  }, [csvFileName, handleCloseActionsMenu, table, tableAugmentation]);

  const getFilteredSortedRowsForExport = React.useCallback(() => {
    const filteredSortedRowIds = gridFilteredSortedRowIdsSelector(apiRef);

    return filteredSortedRowIds.reduce<Array<Record<string, unknown>>>((currentRows, rowId) => {
      const row = apiRef.current.getRow(rowId) as Record<string, unknown> | null | undefined;

      if (row) {
        currentRows.push(row);
      }

      return currentRows;
    }, []);
  }, [apiRef]);

  const handleDownloadFilteredCsv = React.useCallback(() => {
    handleCloseActionsMenu();

    const filteredRows = getFilteredSortedRowsForExport();
    const csvContent = buildStudyReportTableCsvContent(table, filteredRows, tableAugmentation);
    const csvBlob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });

    downloadBlob(csvBlob, `${csvFileName}-filtered.csv`);
  }, [csvFileName, getFilteredSortedRowsForExport, handleCloseActionsMenu, table, tableAugmentation]);

  const handleOpenFullscreen = React.useCallback(() => {
    setIsFullscreenOpen(true);
  }, []);

  const handleFilterModelChange = React.useCallback((nextFilterModel: GridFilterModel) => {
    setFilterModel(nextFilterModel);
    setPaginationModel((currentPaginationModel) =>
      currentPaginationModel.page === 0 ? currentPaginationModel : { ...currentPaginationModel, page: 0 }
    );
  }, []);

  const handleCloseFullscreen = React.useCallback(() => {
    setIsFullscreenOpen(false);
    handleCloseActionsMenu();
  }, [handleCloseActionsMenu]);

  const renderTableGrid = React.useCallback(
    ({ fullScreen = false }: { fullScreen?: boolean } = {}) => {
      const shouldUseAutoHeight = !fullScreen && fitsOnOnePage && rows.length > 0 && !hasActiveFilters;

      return (
        <Box
          data-testid="study-report-table-grid-container"
          sx={{
            width: '100%',
            ...(fullScreen
              ? { height: 'calc(100vh - 240px)' }
              : shouldUseAutoHeight
              ? {}
              : {
                  height: fitsOnOnePage ? TABLE_EMPTY_STATE_GRID_HEIGHT : 480,
                  minHeight: TABLE_EMPTY_STATE_GRID_HEIGHT,
                }),
          }}
        >
          <ReadOnlyDataGrid
            apiRef={apiRef}
            pagination
            paginationMode="client"
            rows={rows}
            columns={columns}
            autosizeOnMount={!DISABLE_VIRTUALIZATION_FOR_TESTS}
            autosizeOptions={autosizeOptions}
            autoHeight={shouldUseAutoHeight}
            density={density}
            onDensityChange={setDensity}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            initialState={{ pagination: { paginationModel: { pageSize: TABLE_PAGE_SIZE, page: 0 } } }}
            pageSizeOptions={[10, 25, 50, 100]}
            hideFooter={!fullScreen && fitsOnOnePage}
            disableRowSelectionOnClick
            disableVirtualization={DISABLE_VIRTUALIZATION_FOR_TESTS}
            hasActiveFilters={hasActiveFilters}
            noResultsTitle="No results match current filters"
            noResultsSubtitle="Try changing or clearing the active filters to see rows again."
            overlayHeight={fullScreen ? FULLSCREEN_TABLE_OVERLAY_HEIGHT : TABLE_OVERLAY_HEIGHT}
            quickFilterDebounceMs={250}
            sx={{
              '& .MuiDataGrid-cell': {
                fontWeight: 600,
              },
            }}
          />
        </Box>
      );
    },
    [
      apiRef,
      autosizeOptions,
      columns,
      density,
      filterModel,
      fitsOnOnePage,
      handleFilterModelChange,
      hasActiveFilters,
      paginationModel,
      rows,
    ]
  );

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <StudyReportCardHeader
            title={table.title}
            description={
              displayDescription ? (
                <StudyReportDescriptionMarkdown content={displayDescription} studyId={studyId} />
              ) : null
            }
            actions={
              <>
                <IconButton
                  size="small"
                  onClick={handleOpenFullscreen}
                  aria-label="Open full screen"
                  disabled={!hasRenderableContent}
                >
                  <FullscreenRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleOpenActionsMenu}
                  aria-label="Table actions"
                  disabled={!hasRenderableContent}
                >
                  <IosShareRoundedIcon fontSize="small" />
                </IconButton>
                {action}
                {onRemove ? (
                  <IconButton size="small" onClick={onRemove}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
              </>
            }
          />

          {beforeGrid || showSlideLabelControl ? (
            <Stack spacing={1}>
              {beforeGrid}
              {showSlideLabelControl ? (
                <JobResultSlideLabelModeSelect
                  value={slideLabelMode}
                  options={slideLabelOptions}
                  onChange={setSlideLabelMode}
                />
              ) : null}
            </Stack>
          ) : null}

          {renderTableGrid()}

          {resolvedSourceLabel ? (
            <Chip
              label={resolvedSourceLabel}
              size="small"
              variant="outlined"
              sx={{ width: 'fit-content', textTransform: 'capitalize' }}
            />
          ) : null}
        </Stack>
      </Paper>

      <Menu anchorEl={actionsAnchorEl} open={actionsMenuOpen} onClose={handleCloseActionsMenu}>
        {hasActiveFilters ? (
          <MenuItem onClick={handleDownloadFilteredCsv} disabled={!hasRenderableContent}>
            <ListItemIcon>
              <FilterAltRoundedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Download filtered CSV</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem onClick={handleDownloadCsv} disabled={!hasRenderableContent}>
          <ListItemIcon>
            <TableChartRoundedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Download CSV</ListItemText>
        </MenuItem>
      </Menu>

      <Dialog
        open={isFullscreenOpen}
        onClose={handleCloseFullscreen}
        fullScreen
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: 'background.default',
          },
        }}
      >
        <DialogTitle sx={{ pb: table.description ? 1.5 : 2 }}>
          <Grid container alignItems="flex-start" justifyContent="space-between" spacing={1.5}>
            <Grid item xs>
              <Typography variant="h2">{table.title || 'Table'}</Typography>
              {displayDescription ? (
                <StudyReportDescriptionMarkdown
                  content={displayDescription}
                  studyId={studyId}
                  sx={{ mt: 1.25, maxWidth: 960 }}
                />
              ) : null}
            </Grid>
            <Grid item>
              <Stack direction="row" spacing={0.5}>
                <IconButton size="small" onClick={handleOpenActionsMenu} aria-label="Table actions">
                  <IosShareRoundedIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={handleCloseFullscreen} aria-label="Close">
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5}>
            {beforeGrid}
            {showSlideLabelControl ? (
              <JobResultSlideLabelModeSelect
                value={slideLabelMode}
                options={slideLabelOptions}
                onChange={setSlideLabelMode}
              />
            ) : null}
            {renderTableGrid({ fullScreen: true })}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StudyReportSectionTableCard;
